/**
 * 竞价投放 - 计划/任务等维度数据（使用 Redis token 拉取华为广告）
 */
import request from '@/utils/request'

/** 竞价「全部账户」按户拉取时较慢，统一延长超时（3 分钟） */
const BIDDING_TIMEOUT = 180000
/** 快照读接口：读 DB 快照，响应快，短超时即可 */
const SNAPSHOT_TIMEOUT = 15000

function buildRepeatedQuery(params, arrayKeys = []) {
  const parts = []
  const keySet = new Set(arrayKeys || [])
  Object.keys(params || {}).forEach(key => {
    const val = params[key]
    if (val === undefined || val === null || val === '') return
    if (keySet.has(key) && Array.isArray(val)) return
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(val))}`)
  })
  keySet.forEach(key => {
    const arr = params && Array.isArray(params[key]) ? params[key] : []
    arr.forEach(item => {
      const v = item != null ? String(item).trim() : ''
      if (v) parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
    })
  })
  return parts.join('&')
}

/**
 * 可切换的经理户列表（来自 subject_token 表，纯数据库读取）
 */
/**
 * @param {Object} [params] - platformType: jh | jl | baidu | iqiyi（与主体 token、账户列表页签一致；默认 jh）
 */
export function getManagerList(params) {
  return request({ url: '/api/bidding/managers', method: 'get', params: params || {}, timeout: 15000 })
}

/**
 * 经理户下属广告主列表（token 约半小时更新一次，allPages 时可能较慢）
 * @param {Object} params - managerAccountId, pageNum, pageSize, allPages
 */
export function getAdvertiserList(params) {
  return request({
    url: '/api/bidding/advertisers',
    method: 'get',
    params,
    timeout: BIDDING_TIMEOUT
  })
}

/**
 * 查询版位（后端会从 Redis 取 token 再调华为）
 * @param {Object} params - managerAccountId, advertiserId, productType, campaignType
 */
export function getPositionList(params) {
  return request({
    url: '/api/bidding/position-probe',
    method: 'get',
    params,
    timeout: 30000
  })
}

/**
 * 按版位拉取鲸鸿按钮文案（select_id + text），用于核对「点击按钮」与鲸鸿后台展示是否一致
 * @param {Object} params - managerAccountId, advertiserId, creativeSizeId, productType, objectiveType, language
 * @returns {Promise<{ list: Array<{ selectId, text, language }> }>}
 */
export function getPositionButtonTexts(params) {
  return request({
    url: '/api/bidding/position-button-texts',
    method: 'get',
    params: {
      managerAccountId: params.managerAccountId,
      advertiserId: params.advertiserId,
      creativeSizeId: params.creativeSizeId,
      productType: params.productType || 'QUICK_APP',
      objectiveType: params.objectiveType,
      language: params.language
    },
    timeout: 30000
  })
}

/**
 * 查询转化跟踪目标（华为 effect_tracking/query），供定向配置「转化跟踪ID」下拉拉取
 * @param {Object} params - managerAccountId, advertiserId, page, pageSize
 * @returns {Promise<{ total: number, list: Array<{ trackingId, effectName, effectType, trackingStatus }> }>}
 */
export function getEffectTrackingList(params) {
  return request({
    url: '/api/bidding/effect-tracking',
    method: 'get',
    params: {
      managerAccountId: params.managerAccountId,
      advertiserId: params.advertiserId,
      page: params.page != null ? params.page : 1,
      pageSize: params.pageSize != null ? params.pageSize : 50
    },
    timeout: 30000
  })
}

/**
 * 查询已入库的转化跟踪目标（platform_account_trantype）
 * @param {Object} params - advertiserId
 */
export function getEffectTrackingCached(params) {
  return request({
    url: '/api/bidding/effect-tracking/cached',
    method: 'get',
    params: {
      advertiserId: params.advertiserId
    },
    timeout: 15000
  })
}

/**
 * 账户维度：拉取广告主数据（后端从 Redis 取 token 调华为 4.31 广告主数据接口）
 */
export function getAccountList(params, options = {}) {
  const { accountIdTokens, qaAgentUserNames, managerAccountIds, ...rest } = params || {}
  const config = {
    url: '/api/bidding/accounts',
    method: 'get',
    params: {
      ...rest,
      accountIdTokens,
      qaAgentUserNames,
      managerAccountIds
    },
    timeout: BIDDING_TIMEOUT,
    skipErrorHandler: options.silent === true
  }
  config.paramsSerializer = queryParams => buildRepeatedQuery(queryParams, ['accountIdTokens', 'qaAgentUserNames', 'managerAccountIds'])
  return request(config)
}

/** 账户维度报表 CSV 导出：单次拉取当前筛选下全部行（上限 5 万） */
export function getAccountExportRows(params, options = {}) {
  const { accountIdTokens, qaAgentUserNames, managerAccountIds, ...rest } = params || {}
  const config = {
    url: '/api/bidding/accounts/export-rows',
    method: 'get',
    params: {
      ...rest,
      accountIdTokens,
      qaAgentUserNames,
      managerAccountIds
    },
    timeout: BIDDING_TIMEOUT,
    skipErrorHandler: options.silent === true
  }
  config.paramsSerializer = queryParams => buildRepeatedQuery(queryParams, ['accountIdTokens', 'qaAgentUserNames', 'managerAccountIds'])
  return request(config)
}

/** 账户维度报表 CSV 导出审计（写入 sys_operation_log） */
export function recordBiddingAccountExportAudit(data) {
  return request({
    url: '/api/bidding/accounts/export-audit',
    method: 'post',
    data: data || {},
    timeout: 15000
  })
}

/** 账户质量分（时分）：主列表加载后异步补齐，不阻塞 /accounts */
export function getAccountQualityScores(params, options = {}) {
  return request({
    url: '/api/bidding/accounts-quality-scores',
    method: 'get',
    params,
    timeout: 30000,
    skipErrorHandler: options.silent === true
  })
}

/** 快照汇总（读 DB，用于首屏快速展示） */
export function getSnapshotSummary(params) {
  return request({ url: '/api/bidding/snapshot/summary', method: 'get', params, timeout: SNAPSHOT_TIMEOUT })
}

/** 快照-账户维度 */
export function getSnapshotAccountList(params) {
  return request({ url: '/api/bidding/snapshot/accounts', method: 'get', params, timeout: SNAPSHOT_TIMEOUT })
}

/** 快照-计划维度 */
export function getSnapshotPlanList(params) {
  return request({ url: '/api/bidding/snapshot/plans', method: 'get', params, timeout: SNAPSHOT_TIMEOUT })
}

/** 快照-任务维度 */
export function getSnapshotTaskList(params) {
  return request({ url: '/api/bidding/snapshot/tasks', method: 'get', params, timeout: SNAPSHOT_TIMEOUT })
}

/** 快照-创意维度 */
export function getSnapshotCreativeList(params) {
  return request({ url: '/api/bidding/snapshot/creatives', method: 'get', params, timeout: SNAPSHOT_TIMEOUT })
}

/** 发起快照刷新任务（后台异步拉取，立即返回） */
export function postSnapshotRefresh(params) {
  return request({ url: '/api/bidding/snapshot/refresh', method: 'post', params, timeout: 10000 })
}

/** 查询快照任务状态 */
export function getSnapshotTaskStatus(taskId) {
  return request({ url: `/api/bidding/snapshot/task/${taskId}`, method: 'get', timeout: 5000 })
}

/**
 * 计划维度：拉取计划数据（后端从 Redis 取 token 调华为 4.32 计划数据接口）
 */
export function getPlanList(params, options = {}) {
  return request({
    url: '/api/bidding/plans',
    method: 'get',
    params,
    timeout: BIDDING_TIMEOUT,
    skipErrorHandler: options.silent === true
  })
}

/**
 * 任务维度：拉取任务数据（华为 4.33 任务数据接口）
 * 支持账户侧筛参：accountIdTokens / campaignIdTokens / qaAgentUserNames（需重复 key 序列化）
 */
export function getTaskList(params, options = {}) {
  const {
    accountIdTokens,
    campaignIdTokens,
    keywordTokens,
    qaAgentUserNames,
    managerAccountIds,
    ...rest
  } = params || {}
  const config = {
    url: '/api/bidding/tasks',
    method: 'get',
    params: {
      ...rest,
      accountIdTokens,
      campaignIdTokens,
      keywordTokens,
      qaAgentUserNames,
      managerAccountIds
    },
    timeout: BIDDING_TIMEOUT,
    skipErrorHandler: options.silent === true
  }
  config.paramsSerializer = queryParams => buildRepeatedQuery(queryParams, [
    'accountIdTokens',
    'campaignIdTokens',
    'keywordTokens',
    'qaAgentUserNames',
    'managerAccountIds'
  ])
  return request(config)
}

/**
 * 鲸鸿任务投放日期下拉（按统计日期窗去重，不区分经理户）
 */
export function getJhTaskPlacementDateOptions(params) {
  return request({
    url: '/api/bidding/jh/task-placement-date-options',
    method: 'get',
    params,
    timeout: BIDDING_TIMEOUT
  })
}

/**
 * 创意维度：拉取创意数据（华为 4.34 创意数据接口）
 */
export function getCreativeList(params, options = {}) {
  return request({
    url: '/api/bidding/creatives',
    method: 'get',
    params,
    timeout: BIDDING_TIMEOUT,
    skipErrorHandler: options.silent === true
  })
}

/**
 * 竞价投放汇总：今日消耗/有消耗账户数/余额不足数、花费/曝光/点击等；支持与账户列表相同的账户级筛选。
 * @param {Object} params - managerAccountId, advertiserId, startDate, endDate, qaAgentUserNames, accountIdTokens, accountNameKeyword, deliveryAppNameKeyword, operatorUserId, deliveryLandingPageId
 */
export function getBiddingSummary(params) {
  return request({
    url: '/api/bidding/summary',
    method: 'get',
    params,
    timeout: BIDDING_TIMEOUT,
    paramsSerializer: queryParams => buildRepeatedQuery(queryParams, [
      'qaAgentUserNames',
      'managerAccountIds',
      'accountIdTokens',
      'keywordTokens',
      'campaignIdTokens',
      'excludeIdTokens'
    ])
  })
}

/** 巨量：代理商名称（与投放配置 QA 下拉同源，竞价账户筛选） */
export function listJlQaAgentNames() {
  return request({ url: '/api/bidding/jl-qa-agent-names', method: 'get', timeout: 10000 })
}

/** 巨量：投放产品名称（投放配置 + 账户报表去重，竞价账户筛选） */
export function listJlDeliveryAppNames() {
  return request({ url: '/api/bidding/jl-delivery-app-names', method: 'get', timeout: 10000 })
}

/** 竞价投放：按媒体获取投放产品名称（账户筛选下拉） */
export function listDeliveryAppNames(params) {
  return request({
    url: '/api/bidding/delivery-app-names',
    method: 'get',
    params: params || {},
    timeout: 10000
  })
}

/**
 * 工作台顶栏：当前账号可见经理户「今日」花费、IAA 求和、ROI（IAA/花费），与竞价页筛选无关
 */
export function getGlobalTodaySummary() {
  return request({ url: '/api/bidding/summary/global-today', method: 'get', timeout: 15000 })
}

/**
 * 快照账户列表（与 /bidding/advertisers 结构一致），有快照时优先用此接口保证下拉与搜索完整
 * @param {Object} params - managerAccountId, advertiserId, startDate, endDate
 */
export function getSnapshotAdvertisers(params) {
  return request({ url: '/api/bidding/snapshot/advertisers', method: 'get', params, timeout: SNAPSHOT_TIMEOUT })
}

/**
 * 账户详情列表：仅从数据库缓存读取（由每日 0 点定时任务更新），支持分页与账户ID/事件ID 为空或不为空筛选
 * @param {Object} params - managerAccountId, pageNum, pageSize, advertiserIdFilter(all|empty|not_empty), eventIdFilter(all|empty|not_empty), keyword(可选), accountIdTokens(可选数组，广告主ID 多值模糊 OR)
 */
export function getAccountsInfo(params) {
  const { accountIdTokens, ...rest } = params || {}
  const config = {
    url: '/api/bidding/accounts-info',
    method: 'get',
    params: {
      managerAccountId: rest.managerAccountId,
      platformType: rest.platformType,
      pageNum: rest.pageNum,
      pageSize: rest.pageSize,
      advertiserIdFilter: rest.advertiserIdFilter,
      eventIdFilter: rest.eventIdFilter,
      keyword: rest.keyword,
      autoJudgeTagCode: rest.autoJudgeTagCode,
      accountIdTokens
    },
    timeout: 15000
  }
  config.paramsSerializer = queryParams => buildRepeatedQuery(queryParams, ['accountIdTokens'])
  return request(config)
}

/**
 * 手动触发该经理户下账户基础信息全量拉取（仅账户ID+昵称），速度较快
 * @param {Object} params - managerAccountId
 */
export function refreshAccountsInfo(params) {
  return request({
    url: '/api/bidding/accounts-info/refresh',
    method: 'post',
    params: { managerAccountId: params.managerAccountId },
    timeout: 120000
  })
}

/**
 * 巨量：从媒体拉取组织下账户列表写入 platform_account（与鲸鸿 refresh 接口分离）
 * @param {Object} params - managerAccountId 组织 ID
 */
export function refreshJlPlatformAccountsBasic(params) {
  return request({
    url: '/api/bidding/platform-accounts/jl/refresh-basic',
    method: 'post',
    params: { managerAccountId: params.managerAccountId },
    timeout: 180000
  })
}

/**
 * 巨量：同步单账户事件资产并回写 event_id
 * @param {Object} params - managerAccountId, advertiserId
 */
export function syncJlAccountEvents(params) {
  return request({
    url: '/api/bidding/platform-accounts/jl/events/sync',
    method: 'post',
    params: { managerAccountId: params.managerAccountId, advertiserId: params.advertiserId },
    timeout: 120000
  })
}

/**
 * 巨量：查询单账户已落库事件资产
 * @param {Object} params - managerAccountId, advertiserId
 */
export function listJlAccountEvents(params) {
  return request({
    url: '/api/bidding/platform-accounts/jl/events',
    method: 'get',
    params: { managerAccountId: params.managerAccountId, advertiserId: params.advertiserId },
    timeout: 20000
  })
}

/**
 * 百度：从媒体拉取组织下账户列表写入 platform_account（与鲸鸿 refresh 接口分离）
 * @param {Object} params - managerAccountId 组织 ID
 */
export function refreshBaiduPlatformAccountsBasic(params) {
  return request({
    url: '/api/bidding/platform-accounts/baidu/refresh-basic',
    method: 'post',
    params: { managerAccountId: params.managerAccountId },
    timeout: 180000
  })
}

/**
 * 爱奇艺：从媒体拉取组织下账户列表写入 platform_account
 * @param {Object} params - managerAccountId 组织 ID
 */
export function refreshIqiyiPlatformAccountsBasic(params) {
  return request({
    url: '/api/bidding/platform-accounts/iqiyi/refresh-basic',
    method: 'post',
    params: { managerAccountId: params.managerAccountId },
    timeout: 180000
  })
}

/**
 * 爱奇艺：advertiser/info 回写 account_name 及联系人
 * @param {Object} params - managerAccountId 组织 ID
 */
export function refreshIqiyiPlatformAccountsDetail(params) {
  return request({
    url: '/api/bidding/platform-accounts/iqiyi/refresh-details',
    method: 'post',
    params: { managerAccountId: params.managerAccountId },
    timeout: 180000
  })
}

/**
 * 快手：从媒体拉取代理商下账户列表写入 platform_account
 * @param {Object} params - managerAccountId 组织 ID（agent_id）
 */
export function refreshKuaishouPlatformAccountsBasic(params) {
  return request({
    url: '/api/bidding/platform-accounts/kuaishou/refresh-basic',
    method: 'post',
    params: { managerAccountId: params.managerAccountId },
    timeout: 180000
  })
}

/**
 * 手动触发该经理户下账户详情（联系人、推广内容、事件ID、App信息）补拉。
 * 后端已按批 no_review + 有限并发，户多时仍可能数分钟。
 * @param {Object} params - managerAccountId
 */
export function refreshAccountsDetail(params) {
  return request({
    url: '/api/bidding/accounts-info/refresh-details',
    method: 'post',
    params: { managerAccountId: params.managerAccountId },
    timeout: 600000
  })
}

/**
 * 按广告主 ID 单独拉取一个账户详情并落库，展示在账户列表中
 * @param {Object} params - managerAccountId, advertiserId
 */
/**
 * 批量导入命名备注（模板：账户id + 命名备注）
 * @param {Object} data - { managerAccountId, rows: [{ advertiserId, namingRemark }, ...] }
 */
export function batchUpdateNamingRemark(data) {
  return request({
    url: '/api/bidding/accounts-info/batch-naming-remark',
    method: 'post',
    data,
    timeout: 30000
  })
}

/**
 * 批量导入 App 信息（模板：账户id + app_id + 包名）
 * @param {Object} data - { managerAccountId, rows: [{ advertiserId, appId, appPackage }, ...] }
 */
export function batchUpdateAppInfo(data) {
  return request({
    url: '/api/bidding/accounts-info/batch-app-info',
    method: 'post',
    data,
    timeout: 30000
  })
}

/**
 * 批量导入产品名称（存 platform_account_info 表，有则更新、无则插入）
 * @param {Object} data - { managerAccountId, rows: [{ advertiserId, productName }, ...] }
 */
export function batchUpdateProductName(data) {
  return request({
    url: '/api/bidding/accounts-info/batch-product-name',
    method: 'post',
    data,
    timeout: 30000
  })
}

/**
 * 批量导入密钥（存 platform_account_info.token，有则更新、无则插入）
 * @param {Object} data - { managerAccountId, rows: [{ advertiserId, token }, ...] }
 */
export function batchUpdateToken(data) {
  return request({
    url: '/api/bidding/accounts-info/batch-token',
    method: 'post',
    data,
    timeout: 30000
  })
}

export function refreshOneAccount(params) {
  return request({
    url: '/api/bidding/accounts-info/refresh-one',
    method: 'post',
    params: {
      managerAccountId: params.managerAccountId,
      advertiserId: params.advertiserId
    },
    timeout: 30000
  })
}

/**
 * 批量更新账户日预算（华为 4.36）
 * @param {Object} data - { managerAccountId, advertiserIds: string[], dailyBudget: number, dailyBudgetOpType?: string }
 */
export function batchUpdateAccountDailyBudget(data) {
  return request({
    url: '/api/bidding/accounts/daily-budget/batch',
    method: 'post',
    data,
    timeout: 60000
  })
}

/**
 * 任务列表（华为 adgroup/query，含状态、期望转化成本等，用于合并到报表数据）
 */
export function getAdgroupQueryList(params) {
  return request({
    url: '/api/bidding/adgroup-query',
    method: 'get',
    params: {
      managerAccountId: params.managerAccountId,
      advertiserId: params.advertiserId,
      campaignId: params.campaignId,
      page: params.page != null ? params.page : 1,
      pageSize: params.pageSize != null ? params.pageSize : 500
    },
    timeout: 30000
  })
}

/**
 * 批量修改任务状态（启用/停用）— 鲸鸿
 * @param {Object} data - { managerAccountId, advertiserId, adgroupIds: string[], opType: 'OPERATION_ENABLE'|'OPERATION_DISABLE' }
 */
export function batchUpdateTaskStatus(data) {
  return request({
    url: '/api/bidding/adgroup/batch-status',
    method: 'post',
    data,
    timeout: 30000
  })
}

/**
 * 巨量单元批量启停
 * @param {Object} data - { managerAccountId, advertiserId, promotionIds: string[], opType }
 */
export function batchUpdateJlPromotionStatus(data) {
  return request({
    url: '/api/bidding/jl/promotion/batch-status',
    method: 'post',
    data,
    timeout: 30000
  })
}

/**
 * 批量拉空：时段设为「仅周六 7–8 点可投」默认 336 位，并记录预计可恢复时间 — 鲸鸿
 * @param {Object} data - { managerAccountId, advertiserId, durationType: 'DAY'|'HOUR', durationValue: number, adgroupIds: string[] }
 */
export function batchUpdateTaskPullEmpty(data) {
  return request({
    url: '/api/bidding/adgroup/batch-pull-empty',
    method: 'post',
    data,
    timeout: 60000
  })
}

/**
 * 巨量单元批量拉空（二期：弹窗 336 位时段 + 按项目 schedule_time/update）
 * @param {Object} data - { managerAccountId, advertiserId, durationType, durationValue, promotionIds, campaignIdByTaskId?, scheduleTime }
 */
export function batchUpdateJlPromotionPullEmpty(data) {
  return request({
    url: '/api/bidding/jl/promotion/batch-pull-empty',
    method: 'post',
    data,
    timeout: 60000
  })
}

/** 拉空记录分页 */
export function getTaskEmptyspaceRecordPage(data) {
  return request({
    url: '/api/bidding/task-emptyspace-record/page',
    method: 'post',
    data,
    timeout: 30000
  })
}

/** 恢复拉空前投放时段；可选 forceEarlyRestore=true 跳过可恢复时间（前端已确认） */
export function restoreTaskEmptyspaceRecord(data) {
  return request({
    url: '/api/bidding/task-emptyspace-record/restore',
    method: 'post',
    data,
    timeout: 60000
  })
}

/** 任务操作日志分页 */
export function getTaskOperateLogPage(data) {
  return request({
    url: '/api/bidding/task-operate-log/page',
    method: 'post',
    data,
    timeout: 30000
  })
}

/**
 * 批量修改任务期望转化成本 / 首ROI系数
 * @param {Object} data - { managerAccountId, advertiserId?, tasks?: [{advertiserId, adgroupId, conversionCost, ...}], accountScopeType?, advertiserIds?, adgroupStatus?, bidTypeFilter: 'COST'|'TROAS', adjustmentGroups? | adjustmentMode+fixedValue/percentAdd }
 */
export function batchUpdateTaskConversionCost(data) {
  return request({
    url: '/api/bidding/adgroup/batch-conversion-cost',
    method: 'post',
    data,
    timeout: 60000
  })
}

/**
 * 批量修改任务投放时间：增加 x 天 / 减少 x 天 / 修改为长期投放
 * @param {Object} data - { managerAccountId, advertiserId, operation: 'ADD_DAYS'|'SUBTRACT_DAYS'|'SET_LONG_TERM', days?: number, tasks: [{ adgroupId, placementBeginDate, placementEndDate }, ...] }
 */
export function batchUpdateTaskPlacementTime(data) {
  return request({
    url: '/api/bidding/adgroup/batch-placement-time',
    method: 'post',
    data,
    timeout: 60000
  })
}

/**
 * OCPX 赔付天报表（华为 queryDailyCompensation）：汇总 + 按日赔付金额 + 明细（实时，排障用）
 * @param {Object} params - managerAccountId, advertiserId（不传或 all=经理户下全部账户）, startDate, endDate, page?, pageSize?, mergeAllPages?, maxAdvertisers?, isAbroad?, adgroupIds?, compensationStatuses?
 */
export function getOcpxDailyCompensation(params) {
  return request({
    url: '/api/bidding/ocpx-daily-compensation',
    method: 'get',
    params,
    timeout: BIDDING_TIMEOUT
  })
}

/**
 * 鲸鸿 OCPX 赔付读库查询（jh_ocpx_compensation_day）
 */
export function getJhOcpxCompensationList(params) {
  return request({
    url: '/api/bidding/ocpx-compensation/list',
    method: 'get',
    params,
    timeout: BIDDING_TIMEOUT
  })
}

/**
 * 鲸鸿 OCPX 赔付异步同步（提交后轮询 status）
 */
export function syncJhOcpxCompensation(data) {
  return request({
    url: '/api/bidding/ocpx-compensation/sync',
    method: 'post',
    data: data || {},
    timeout: 60000
  })
}

/**
 * 鲸鸿 OCPX 赔付同步进度
 */
export function getJhOcpxCompensationSyncStatus(batchId) {
  return request({
    url: '/api/bidding/ocpx-compensation/sync/status',
    method: 'get',
    params: { batchId },
    timeout: 30000
  })
}
